import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaces } from '../lib/queries.js';
import { importLegacy, loadLegacySnapshot, type ImportProgress, type ImportReport } from '../lib/import-legacy.js';

export function ImporterPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { data: workspaces } = useWorkspaces();
  const ws = workspaces?.workspaces.find((w) => w.slug === slug);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [running, setRunning] = useState(false);
  const qc = useQueryClient();
  const snap = loadLegacySnapshot();

  async function run() {
    if (!ws) return;
    setRunning(true);
    setReport(null);
    const r = await importLegacy(ws.id, setProgress);
    setReport(r);
    setRunning(false);
    qc.invalidateQueries({ queryKey: ['documents', ws.id] });
    qc.invalidateQueries({ queryKey: ['links', ws.id] });
  }

  if (!ws) return <div className="p-8 text-zinc-500">workspace não encontrado</div>;

  return (
    <aside className="absolute right-0 top-0 h-full w-[480px] border-l border-zinc-800 bg-zinc-950/95 backdrop-blur p-6 overflow-y-auto">
      <h2 className="text-xl font-semibold text-cyan-400 mb-2">Importar app antigo</h2>
      <p className="text-zinc-500 text-sm mb-6">
        Migra <code>synapse_os_v3</code> do localStorage para este workspace.
        HTML cru passa por DOMPurify e vira ProseMirror JSON. Imagens base64 sobem pro MinIO.
      </p>

      {!snap ? (
        <div className="rounded border border-zinc-800 p-4 text-zinc-500 text-sm">
          Nenhum snapshot encontrado em <code>localStorage["synapse_os_v3"]</code>.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded border border-zinc-800 p-4 text-sm">
            <div>Nós encontrados: <span className="text-cyan-400">{snap.nodes.length}</span></div>
            <div>Links encontrados: <span className="text-cyan-400">{snap.links.length}</span></div>
          </div>

          <button
            onClick={run}
            disabled={running}
            className="rounded-md bg-cyan-500 text-zinc-950 font-medium px-4 py-2 hover:bg-cyan-400 disabled:opacity-50"
          >
            {running ? 'importando…' : 'Iniciar importação'}
          </button>

          {progress && (
            <div className="rounded border border-zinc-800 p-4 text-sm">
              <div className="text-zinc-400 mb-2">
                {progress.step === 'documents' && `Documentos: ${progress.current}/${progress.total}`}
                {progress.step === 'links' && `Links: ${progress.current}/${progress.total}`}
                {progress.step === 'done' && `Concluído: ${progress.current}/${progress.total}`}
              </div>
              <div className="h-2 bg-zinc-900 rounded overflow-hidden">
                <div
                  className="h-full bg-cyan-500 transition-all"
                  style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {report && (
            <div className="rounded border border-emerald-800 bg-emerald-950/30 p-4 text-sm">
              <div className="text-emerald-400 font-medium mb-2">Importação finalizada</div>
              <div>Sucesso: {report.success}/{report.total}</div>
              {report.failed.length > 0 && (
                <details className="mt-2">
                  <summary className="text-rose-400 cursor-pointer">
                    Falhas: {report.failed.length}
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-zinc-400">
                    {report.failed.map((f) => (
                      <li key={f.id}>
                        <code>{f.id}</code>: {f.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
